import { z } from "zod";

import { SURFACE_KEYS } from "./surface";
import { dateSchema } from "./utils";

const ENGAGEMENT_ACTIONS = [
  "favourite",
  "retruth",
  "reply",
  "quote",
  "follow",
  "bookmark",
  "share",
] as const;

const impressionVideoSchema = z.object({
  watched_ms: z.number(),
  completed: z.boolean(),
  sound_on: z.boolean(),
  playback_stalled: z.boolean(),
});

/**
 * Shared envelope for the two session-lifecycle events, POSTed on the same /api/v1/flow
 * transport as impressions. Unlike an impression these carry no status/position/dwell payload —
 * only the identity envelope. snake_case matches the cross-client contract.
 *
 * `experiment_labels` is part of the canonical contract ("echoed when present") but web has no
 * client-side experiment source yet, so it is intentionally never populated (see TMTG-24).
 */
const eventBaseSchema = z.object({
  event_id: z.string(),
  event_time: dateSchema, // ISO8601 UTC ms
  session_id: z.string(),
  client_seq: z.number(),
  device: z.literal("web"),
  app_version: z.string(),
});

/** Emitted once per minted session_id, `event_time` = the mint moment, random UUIDv4 event_id. */
const sessionStartEventSchema = eventBaseSchema.extend({
  event_type: z.literal("session_start"),
});

/**
 * Emitted when a session ends. `event_id` is DETERMINISTIC — UUIDv5(NAMESPACE, lowercased
 * session_id) — so every emission for one session (leave, resume/re-leave, deferred sweep) is
 * byte-identical and the server collapses them to one row. `event_time` is the end moment, or —
 * for a deferred end — the session's last recorded activity.
 */
const sessionEndEventSchema = eventBaseSchema.extend({
  event_type: z.literal("session_end"),
});

/**
 * Wire payload for one impression, POSTed in batches to /api/v1/flow. Validated
 * before send so a malformed event is dropped rather than shipped. snake_case matches the
 * cross-client contract; optional fields are omitted from the payload when absent.
 */
const impressionEventSchema = eventBaseSchema.extend({
  event_type: z.literal("impression"),
  status_id: z.string(),
  first_impression: z.boolean(),
  is_reblog: z.boolean(),
  is_quote: z.boolean(),
  dwell_ms: z.number(), // engaged dwell, not wall-clock
  surface: z.enum(SURFACE_KEYS),
  signature: z.string().optional(), // sevro provenance, echoed verbatim
  author_account_id: z.string().optional(), // omitted when the status has no author
  position: z.number().optional(), // absolute feed depth; omitted off-feed
  announcement_id: z.string().optional(),
  video: impressionVideoSchema.optional(), // video posts only
});

const engagementEventSchema = eventBaseSchema.extend({
  event_type: z.literal("engagement"),
  action: z.enum(ENGAGEMENT_ACTIONS),
  status_id: z.string(),
  author_account_id: z.string().optional(),
  target_account_id: z.string().optional(),
  position: z.number().optional(),
  surface: z.enum(SURFACE_KEYS),
  signature: z.string().optional(),
});

/**
 * Every event that rides the analytics transport. Discriminated on `event_type` so the queue,
 * persistence, and send layers stay type-safe across impression + session events.
 */
const analyticsEventSchema = z.discriminatedUnion("event_type", [
  impressionEventSchema,
  engagementEventSchema,
  sessionStartEventSchema,
  sessionEndEventSchema,
]);

type SessionStartEvent = z.infer<typeof sessionStartEventSchema>;
type SessionEndEvent = z.infer<typeof sessionEndEventSchema>;
type SessionEvent = SessionStartEvent | SessionEndEvent;
type AnalyticsEvent = z.infer<typeof analyticsEventSchema>;

type ImpressionEvent = z.infer<typeof impressionEventSchema>;
type ImpressionVideo = z.infer<typeof impressionVideoSchema>;

type EngagementEvent = z.infer<typeof engagementEventSchema>;
type EngagementAction = (typeof ENGAGEMENT_ACTIONS)[number];

export {
  sessionStartEventSchema,
  sessionEndEventSchema,
  analyticsEventSchema,
  impressionEventSchema,
  engagementEventSchema,
  ENGAGEMENT_ACTIONS,
  type SessionStartEvent,
  type SessionEndEvent,
  type SessionEvent,
  type AnalyticsEvent,
  type ImpressionEvent,
  type ImpressionVideo,
  type EngagementEvent,
  type EngagementAction,
};
